import { FunctionComponent, useEffect } from 'react';
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Table from "react-bootstrap/Table";
import Tab from "react-bootstrap/Tab";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { UserDetails } from '../../component/User';
import { AddRoleButton, ConfirmButton } from '../../component/Button';
import { useUserStore, useAccessStore } from '../../hooks';
import { useParams } from 'react-router-dom';
import { LoadingSpinner } from '../../component/Spinner';
import { UserRole } from '../../model';
import { ROLES } from '../../config';

type Params = {
  username: string
}

const UserDetailsPage: FunctionComponent = () => {
  const params = useParams<Params>();
  const userStore = useUserStore();
  const accessStore = useAccessStore();

  useEffect(() => {
    userStore.fetch(params.username!)
    accessStore.fetchAll();
    return () => userStore.clear();
  }, []);

  const handleNavOnClick = (clientId: string | null) => {
    if (userStore.user && clientId) {
      accessStore.fetchUserAppRoles(userStore.user.sub, clientId);
    }
  }

  const handleAddRole = (role: UserRole) => {
    if (userStore.user && accessStore.application?.clientId) {
      accessStore.addUserRoleToApp(userStore.user.sub, accessStore.application.clientId, role);
    }
  }

  const handleRemoveRole = (role: UserRole) => {
    if (userStore.user && accessStore.application?.clientId) {
      accessStore.removeUserRoleFromApp(userStore.user.sub, accessStore.application.clientId, role);
    }
  }

  if (userStore.loading) {
    return (
      <Container>
        <Row>
          <Col>
            <LoadingSpinner />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container>
      <Row>
        <Col>
          <h2>User Details</h2>
          <UserDetails user={userStore.user} />
        </Col>
      </Row>

      <Row>
        <Col>
          <h3>Access Management</h3>
          <Tab.Container unmountOnExit={true} onSelect={handleNavOnClick}>
            <Row>
              <Col xs={2}>
                <Nav variant="pills" className="flex-column">
                  {accessStore.applications.map((app) => (
                    <Nav.Item key={app.clientId}>
                      <Nav.Link eventKey={app.clientId} disabled={accessStore.loadingRoles}>
                        {app.name}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Col>
              <Col xs={10}>
                { accessStore.application && (
                  <Tab.Content>
                    <Tab.Pane key={accessStore.application.clientId} eventKey={accessStore.application.clientId}>
                      <Table>
                        <thead>
                          <tr>
                            <th>Role</th>
                            <th>Partner</th>
                            <th>Rights</th>
                            <th>
                              <AddRoleButton 
                                roles={ROLES[accessStore.application.clientId]}
                                onAddRole={handleAddRole}
                              />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {accessStore.loadingRoles && (
                            <tr>
                              <td colSpan={4}>
                                <LoadingSpinner />
                              </td>
                            </tr>
                          )}
                          {accessStore.roles?.map((role, index) => (
                            <tr key={index.toString()}>
                              <td>{role.role}</td>
                              <td>{role.partner}</td>
                              <td>{role.rights.join(', ')}</td>
                              <td>
                                <ConfirmButton 
                                  size="sm"
                                  varient="outline-danger" 
                                  title="Are you sure?"
                                  message={`This will remove ${role.role} and associated rights from ${userStore.user?.email}`}
                                  onConfirm={() => handleRemoveRole(role)}
                                >
                                  Remove
                                </ConfirmButton>
                              </td>
                            </tr>
                          ))}  
                        </tbody>
                      </Table>
                    </Tab.Pane>
                  </Tab.Content>
                )}
              </Col>
            </Row>
          </Tab.Container>
        </Col>
      </Row>
    </Container>
  );
};

export default UserDetailsPage;
